'use client';

import { c as _c } from "react/compiler-runtime";
import type { Api, Vehicle } from '@cruz-com/lookup';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { HiArrowUturnLeft } from 'react-icons/hi2';
import { twMerge } from 'tailwind-merge';
import { vehicleApi } from '@/api';
import { Button, Checkbox, CruzIcon, ListBox, PageLoading, Text, Toggle } from '@/components';
import { radiusOption } from '@/settings';
import { useGlobalStore } from '@/store/provider';
import { VehicleMakeModel } from './vehicleMakeModel';
type Props = {
  type?: 'antique';
  ignoreFilters?: string[];
  defaultQuery: Vehicle.Query;
  onFilterChangedAction: (query: Vehicle.Query) => void;
};
const groupStyle = 'w-full divide-y border rounded-md bg-zinc-100/80 dark:bg-zinc-800/80';
export function VehicleFilterMin(t0) {
  const $ = _c(95);
  const {
    type,
    ignoreFilters,
    defaultQuery,
    onFilterChangedAction
  } = t0;
  const {
    scrollTopOver
  } = useGlobalStore(_temp);
  const [mounted, setMounted] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [query, setQuery] = useState(defaultQuery);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      zip_code: "",
      radius: -1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [loc, setLoc] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMounted(true);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== defaultQuery) {
    t4 = () => {
      setTimeout(() => {
        setQuery(defaultQuery);
        setLoc(_.pick(defaultQuery, ["zip_code", "radius"]));
      }, 10);
    };
    t5 = [defaultQuery];
    $[3] = defaultQuery;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const t6 = `vehicleFilterOptions_${type || "none"}`;
  let t7;
  if ($[6] !== t6) {
    t7 = [t6];
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== query || $[9] !== type) {
    t8 = () => vehicleApi.getFilterOption(_.pickBy(query, _.identity), type);
    $[8] = query;
    $[9] = type;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = {
      queryKey: t7,
      queryFn: t8,
      staleTime: 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false
    };
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data,
    isFetching,
    refetch
  } = useQuery(t9);
  let t10;
  if ($[14] !== query) {
    t10 = function checkboxChanged(t11) {
      const {
        key,
        id,
        val
      } = t11;
      const d = _.get(query, key);
      const x = d ? _.split(d, ",") : [];
      const y = val ? _.concat(x, `${id}`) : _.without(x, `${id}`);
      if (_.isEmpty(y)) {
        return setQuery(_.omit(query, key));
      }
      setQuery({
        ...query,
        [key]: _.join(y, ",")
      });
    };
    $[14] = query;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const checkboxChanged = t10;
  let t11;
  if ($[16] !== query) {
    t11 = function radioChanged(t12) {
      const {
        name,
        id: id_0
      } = t12;
      return setQuery({
        ...query,
        [name]: id_0
      });
    };
    $[16] = query;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const radioChanged = t11;
  let t12;
  if ($[18] !== query) {
    t12 = function toggleChanged(name_0, isEnabled) {
      if (isEnabled) {
        return setQuery({
          ...query,
          [name_0]: 1
        });
      }
      setQuery(_.omit(query, name_0));
    };
    $[18] = query;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const toggleChanged = t12;
  let t13;
  if ($[20] !== query) {
    t13 = function mmChanged(name_1, val_0) {
      if (!_.get(query, name_1) && !val_0) {
        return;
      }
      if (_.isEmpty(val_0)) {
        return setQuery(_.omit(query, name_1));
      }
      setQuery({
        ...query,
        [name_1]: val_0
      });
    };
    $[20] = query;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const mmChanged = t13;
  let t14;
  if ($[22] !== loc || $[23] !== query) {
    t14 = function locChanged(name_2, value) {
      let updateValue = value;
      if (name_2 === "zip_code") {
        updateValue = (value as string).replace(/\D/g, "");
      }
      const d_0 = {
        ...loc,
        [name_2]: updateValue
      };
      setLoc(d_0);
      const zLen = _.size(d_0.zip_code);
      if (zLen === 5 || zLen === 0 && _.size(loc.zip_code) > 0) {
        if ((d_0.radius || 0) > 0 || (d_0.radius || 0) < 0 && (loc.radius || 0) > 0) {
          setQuery({
            ...query,
            ...d_0
          });
        }
      }
    };
    $[22] = loc;
    $[23] = query;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const locChanged = t14;
  let t15;
  if ($[25] !== onFilterChangedAction || $[26] !== query) {
    t15 = function applyFilter() {
      setShowFilter(false);
      _.keys(query).forEach(key_0 => {
        const k = key_0 as keyof Vehicle.Query;
        const d_1 = _.toNumber(query[k]);
        if (d_1 < 0) {
          delete query[k];
        }
      });
      setTimeout(() => {
        onFilterChangedAction(query);
      }, 200);
    };
    $[25] = onFilterChangedAction;
    $[26] = query;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const applyFilter = t15;
  let t16;
  if ($[28] !== defaultQuery || $[29] !== query) {
    t16 = () => {
      if (_.isEmpty(defaultQuery) && !_.isEmpty(query)) {
        setQuery({});
      }
    };
    $[28] = defaultQuery;
    $[29] = query;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== defaultQuery) {
    t17 = [defaultQuery];
    $[31] = defaultQuery;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  useEffect(t16, t17);
  let t18;
  if ($[33] !== refetch) {
    t18 = () => {
      refetch();
    };
    $[33] = refetch;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== query) {
    t19 = [query];
    $[35] = query;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  useEffect(t18, t19);
  let t20;
  if ($[37] !== data?.data || $[38] !== query) {
    t20 = () => {
      const qKeys = _.pull(_.keys(query), "page_number", "page_size", "sort_id", "sort_value");
      _.forEach(qKeys, key_1 => {
        const lkp = _.get(data?.data, key_1);
        if (!lkp) {
          return;
        }
        const q = _.split(_.get(query, key_1) as string, ",");
        const nq = [];
        _.forEach(q, id_1 => {
          const x_0 = _.find(lkp, d_2 => d_2?.id?.toString() === id_1?.toString());
          if (x_0) {
            nq.push(id_1);
          }
        });
        if (nq.length > 0) {
          _.assign(query, {
            [key_1]: _.join(nq, ",")
          });
        }
      });
    };
    $[37] = data?.data;
    $[38] = query;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = data?.data;
  let t22;
  if ($[40] !== t21) {
    t22 = [t21];
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  useEffect(t20, t22);
  let t23;
  if ($[42] !== data?.data) {
    t23 = data?.data || {};
    $[42] = data?.data;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const {
    sale_status,
    price,
    mileage,
    year_min,
    year_max,
    make
  } = t23;
  let t24;
  if ($[44] !== year_max || $[45] !== year_min) {
    t24 = () => {
      if (!year_min || !year_max) {
        return [{
          id: -1,
          name: "Any"
        }];
      }
      const d_3 = _.map(_.range(year_max, year_min || 1900), _temp2);
      return [{
        id: -1,
        name: "Any"
      }, ...d_3];
    };
    $[44] = year_max;
    $[45] = year_min;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  const yearOption = t24;
  let t25;
  if ($[47] !== checkboxChanged || $[48] !== ignoreFilters || $[49] !== query?.sale_status || $[50] !== sale_status) {
    t25 = () => ignoreFilters?.includes("sale_status") ? null : <fieldset><legend className="filter-label-min mb-2">Sale Status</legend><ul className="flex flex-col gap-x-3 gap-y-1 sm:flex-row">{_.map(sale_status, t26 => {
          const {
            id: id_2,
            name: name_3
          } = t26;
          return <li key={id_2}><Checkbox name={name_3 as string} label={name_3 as string} checked={_.split((query?.sale_status || "") as string, ",").includes((id_2 || "").toString())} onChange={val_1 => checkboxChanged({
              key: "sale_status",
              id: id_2 as string | number,
              val: val_1
            })} /></li>;
        })}</ul></fieldset>;
    $[47] = checkboxChanged;
    $[48] = ignoreFilters;
    $[49] = query?.sale_status;
    $[50] = sale_status;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const SaleStatus = t25;
  let t26;
  if ($[52] !== query?.year_max || $[53] !== query?.year_min || $[54] !== radioChanged || $[55] !== yearOption) {
    t26 = () => <fieldset><legend className="filter-label-min mb-1.5">Year</legend><div className="flex w-fit items-end gap-x-2"><div className="w-28"><ListBox compact={true} option={yearOption()} value={query?.year_min || -1} className="w-32" onChange={val_2 => {
            radioChanged({
              name: "year_min",
              id: val_2 as number
            });
          }} /></div><div className="text-sm/8 font-light">to</div><div className="w-28"><ListBox compact={true} option={yearOption()} value={query?.year_max || -1} className="w-32" onChange={val_3 => {
            radioChanged({
              name: "year_max",
              id: val_3 as number
            });
          }} /></div></div></fieldset>;
    $[52] = query?.year_max;
    $[53] = query?.year_min;
    $[54] = radioChanged;
    $[55] = yearOption;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const Year = t26;
  let t27;
  if ($[57] !== price || $[58] !== query?.price_max || $[59] !== query?.price_min || $[60] !== radioChanged) {
    t27 = () => <fieldset><legend className="filter-label-min">Price</legend><div className="flex w-fit items-end gap-x-4"><div className="w-36"><ListBox label={<span className="filter-input-label">Minimum</span>} compact={true} option={price || []} value={query?.price_min || -1} className="w-32" onChange={val_4 => {
            radioChanged({
              name: "price_min",
              id: val_4 as number
            });
          }} /></div><div className="w-36"><ListBox label={<span className="filter-input-label">Maximum</span>} compact={true} option={price || []} value={query?.price_max || -1} className="w-32" onChange={val_5 => {
            radioChanged({
              name: "price_max",
              id: val_5 as number
            });
          }} /></div></div></fieldset>;
    $[57] = price;
    $[58] = query?.price_max;
    $[59] = query?.price_min;
    $[60] = radioChanged;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  const Price = t27;
  let t28;
  if ($[62] !== mileage || $[63] !== query?.mileage || $[64] !== radioChanged) {
    t28 = () => <fieldset className="w-64"><legend className="filter-label-min mb-2">Mileage</legend><ListBox compact={true} option={mileage || []} value={query?.mileage || -1} disabled={_.size(mileage) < 2} onChange={val_6 => {
        radioChanged({
          name: "mileage",
          id: val_6 as number
        });
      }} /></fieldset>;
    $[62] = mileage;
    $[63] = query?.mileage;
    $[64] = radioChanged;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const Mileage = t28;
  let t29;
  if ($[66] !== ignoreFilters || $[67] !== loc?.radius || $[68] !== loc?.zip_code || $[69] !== locChanged) {
    t29 = () => ignoreFilters?.includes("location") ? null : <fieldset><legend className="filter-label">Location</legend><div className="mt-2 flex w-fit items-start gap-4"><div className="flex items-center"><label htmlFor="zip_code" className="vehicle-s2-label mr-2">Zip Code:</label><div className="w-[65px]"><Text inline={true} compact={true} name="zip_code" maxLength={5} type="tel" value={loc?.zip_code || ""} onChange={e => locChanged("zip_code", e.target.value)} onFocus={_temp3} /></div></div><div className="flex items-center"><div className="vehicle-s2-label mr-2">Radius:</div><div className="w-[130px]"><ListBox inline={true} compact={true} option={radiusOption} value={loc?.radius || -1} onChange={val_7 => {
              locChanged("radius", val_7);
            }} /></div></div></div></fieldset>;
    $[66] = ignoreFilters;
    $[67] = loc?.radius;
    $[68] = loc?.zip_code;
    $[69] = locChanged;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  const Location = t29;
  let t30;
  if ($[71] !== Location || $[72] !== Mileage || $[73] !== Price || $[74] !== SaleStatus || $[75] !== Year || $[76] !== applyFilter || $[77] !== ignoreFilters || $[78] !== isFetching || $[79] !== make || $[80] !== mmChanged || $[81] !== mounted || $[82] !== query || $[83] !== showFilter || $[84] !== toggleChanged || $[85] !== type) {
    t30 = mounted && <Dialog transition={true} unmount={false} open={showFilter} onClose={() => setShowFilter(false)} className="relative z-30"><DialogBackdrop className="dialog-backdrop" /><div className="fixed inset-0 z-20 w-screen overflow-y-scroll"><div className="mx-auto max-w-[480px]"><DialogPanel className="dialog-panel p-0!"><PageLoading className="top-2" show={isFetching} /><div className="flex w-full flex-col gap-y-6 p-6 md:p-8">{Location()}<Year />{!ignoreFilters?.includes("mm") && <fieldset className="w-full"><legend className="filter-label mb-3">Make Model</legend><div className={groupStyle}><VehicleMakeModel name="mm1" title="Make Model 1" type={type} makeOptions={make || {
                    popular: [],
                    other: []
                  }} otherQuery={query} mm={query?.mm1 || ""} onChange={val_8 => mmChanged("mm1", val_8)} /><VehicleMakeModel name="mm2" title="Make Model 2" type={type} makeOptions={make || {
                    popular: [],
                    other: []
                  }} otherQuery={query} mm={query?.mm2 || ""} onChange={val_9 => mmChanged("mm2", val_9)} /><VehicleMakeModel name="mm3" title="Make Model 3" type={type} makeOptions={make || {
                    popular: [],
                    other: []
                  }} otherQuery={query} mm={query?.mm3 || ""} onChange={val_10 => mmChanged("mm3", val_10)} className="rounded-b-md" /></div></fieldset>}<Price /><Mileage /><SaleStatus />{!ignoreFilters?.includes("with_photo") && <Toggle label="With Photo(s)" name="with_photo" checked={_.toNumber(query?.with_photo) === 1} onChange={isEnabled_0 => toggleChanged("with_photo", isEnabled_0)} />}</div><div className="sticky bottom-0 z-20 rounded-b-lg border-t bg-zinc-600/10 px-6 py-6 backdrop-blur dark:bg-zinc-900"><div className="flex items-center justify-between gap-x-2"><Button type="button" variant="transparent" onClick={() => setQuery({})} aria-label="Reset filters"><HiArrowUturnLeft className="mr-1 size-5" aria-hidden="true" />Reset</Button><Button type="button" variant="primary" className="rounded-full px-6" onClick={applyFilter} aria-label="Apply filters"><CruzIcon.Search className="mr-1 size-5" aria-hidden="true" />Search</Button><Button type="button" variant="transparent" onClick={() => setShowFilter(false)} aria-label="Cancel filter changes"><CruzIcon.Close className="mr-1 size-5" aria-hidden="true" />Cancel</Button></div></div></DialogPanel></div></div></Dialog>;
    $[71] = Location;
    $[72] = Mileage;
    $[73] = Price;
    $[74] = SaleStatus;
    $[75] = Year;
    $[76] = applyFilter;
    $[77] = ignoreFilters;
    $[78] = isFetching;
    $[79] = make;
    $[80] = mmChanged;
    $[81] = mounted;
    $[82] = query;
    $[83] = showFilter;
    $[84] = toggleChanged;
    $[85] = type;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setShowFilter(true);
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Button type="button" variant="secondary" className="mt-4 mb-2 h-11 rounded-full pr-6 pl-5 sm:mt-0 sm:mb-4" onClick={t31} aria-label="Show Search Filters"><CruzIcon.Search className="size-5" />Search Filters</Button>;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== mounted || $[90] !== scrollTopOver) {
    t33 = mounted && <button type="button" className={twMerge("btn-float group w-[46px] transition-all duration-300 ease-in-out hover:w-[145px]", "bottom-[180px] hidden bg-red-800 text-white", scrollTopOver && "lg:block")} onClick={() => setShowFilter(true)} aria-label="Show Serch Filters"><span className="flex items-center gap-x-1 truncate"><CruzIcon.Search className="size-7" /><span className="hidden group-hover:inline">Search Filters</span></span></button>;
    $[89] = mounted;
    $[90] = scrollTopOver;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== t30 || $[93] !== t33) {
    t34 = <>{t30}{t32}{t33}</>;
    $[92] = t30;
    $[93] = t33;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  return t34;
}
function _temp3(e_0) {
  return e_0.target.select();
}
function _temp2(y_0) {
  return {
    id: y_0,
    name: y_0.toString()
  };
}
function _temp(state) {
  return state;
}